import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { popAnimation } from '@app/core/animations/pop';
import { ItemListForFilters } from '@app/shared/forms/models/item-list-for-filters.model';
import { ResponsiveSearchFiltersInterface } from '@app/shared/forms/responsive-search-filters/responsive-search-filters-component.interface';
import { Observable, Subject, debounceTime, fromEvent, merge, takeUntil, timer } from 'rxjs';

export interface ChipFiltersExtraData {
  multiSelect?: boolean;
  rowWrap?: boolean;
  showNavButtons?: boolean;
  enableAnimation?: boolean;
}

/**
 * The directions that scrolling can go in when the header's tabs exceed the header width. 'After'
 * will scroll the header towards the end of the tabs list and 'before' will scroll towards the
 * beginning of the list.
 */
export type ScrollDirection = 'after' | 'before';

/**
 * Amount of milliseconds to wait before starting to scroll the header automatically.
 * Set a little conservatively in order to handle fake events dispatched on touch devices.
 */
const CHIPS_SCROLL_DELAY = 650;

/**
 * Interval in milliseconds at which to scroll the header
 * while the user is holding their pointer.
 */
const CHIPS_SCROLL_INTERVAL = 100;

@Component({
  selector: 'chip-filters',
  templateUrl: './chip-filters.component.html',
  styleUrls: ['./chip-filters.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipFiltersComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [popAnimation],
})
export class ChipFiltersComponent
  implements ControlValueAccessor, ResponsiveSearchFiltersInterface<ItemListForFilters>, OnDestroy, AfterViewInit
{
  @Input() placeHolder: any;
  @Input() showCleaner = true;
  @Input() placeHolderClass = '';
  @Input() iconPrepend = '';
  @Input() disabled = false;
  @Input() initialValue: ItemListForFilters | any;
  @Input() replaceMobileButton = false;
  @Input() isInModal = false;

  @ViewChild('listContainer', { static: true }) protected listContainer?: ElementRef;
  @ViewChildren('chip') protected chipsContainers?: QueryList<ElementRef>[];

  protected itemsSelected: ItemListForFilters[] = [];
  protected internalItems?: ItemListForFilters[];
  protected isMultiSelect = false;
  protected showPaginationControls = signal<boolean>(false);
  protected disableScrollBefore = signal<boolean>(false);
  protected disableScrollAfter = signal<boolean>(false);
  protected showNavigation = signal<boolean>(true);
  protected hasAnimation = signal<boolean>(false);

  protected rowWrap = signal<boolean>(false);
  private unSubscribeAll: Subject<void> = new Subject<void>();
  /** The distance in pixels that the tab labels should be translated to the left. */
  private pScrollDistance = 0;
  /** Stream that will stop the automated scrolling. */
  private stopScrolling = new Subject<void>();

  private ngZone: NgZone = inject(NgZone);
  private elementRef: ElementRef = inject(ElementRef);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  constructor() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(this.elementRef.nativeElement, 'mouseleave')
        .pipe(takeUntil(this.unSubscribeAll))
        .subscribe(() => {
          this.stopInterval();
        });
    });
    fromEvent(this.elementRef.nativeElement, 'resize')
      .pipe(debounceTime(200), takeUntil(this.unSubscribeAll))
      .subscribe(() => this.checkPaginationEnabled());
  }

  @ViewChild('previousPaginator') set previousPaginator(element: ElementRef<HTMLElement>) {
    if (!element) {
      return;
    }
    fromEvent(element.nativeElement, 'touchstart', { passive: true })
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe(() => {
        this.handlePaginatorPress('before');
      });
  }
  @ViewChild('nextPaginator') set nextPaginator(element: ElementRef<HTMLElement>) {
    if (!element) {
      return;
    }
    fromEvent(element.nativeElement, 'touchstart', { passive: true })
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe(() => {
        this.handlePaginatorPress('after');
      });
  }

  get value(): any[] {
    const values = this.itemsSelected.map((item) => item.value);
    return values.length > 0 ? (this.isMultiSelect ? values : values[0]) : null;
  }

  @Input() set items(items: ItemListForFilters[] | Observable<ItemListForFilters[]>) {
    if (items instanceof Observable) {
      items.pipe(takeUntil(this.unSubscribeAll)).subscribe((itemsAsync) => this.handleItems(itemsAsync));
      return;
    }
    this.handleItems(items);
  }

  @Input() set extraData(extraData: ChipFiltersExtraData) {
    if (extraData?.showNavButtons === false) {
      this.showNavigation.set(false);
    }
    this.isMultiSelect = extraData?.multiSelect ?? false;
    this.rowWrap.set(extraData?.rowWrap ?? false);
    this.hasAnimation.set(extraData?.enableAnimation ?? false);
  }

  /** Sets the distance in pixels that the tab header should be transformed in the X-axis. */
  get scrollDistance(): number {
    return this.pScrollDistance;
  }
  set scrollDistance(value: number) {
    this.scrollTo(value);
  }

  /**
   * Handle the on change of input value.
   *
   * @param value
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: any | null) => {};

  /**
   * Handle the touch of the element.
   */
  onTouched = () => {};

  writeValue(value: any | null): void {
    if (!Array.isArray(value)) {
      value = [value];
    }
    this.convertValues(value);
  }

  /**
   * @inheritdoc
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * @inheritdoc
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * @inheritdoc
   */
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngAfterViewInit(): void {
    this.checkPaginationEnabled();
    if (this.listContainer) {
      fromEvent(this.listContainer.nativeElement, 'scroll', { passive: true })
        .pipe(debounceTime(200), takeUntil(this.unSubscribeAll))
        .subscribe(() => {
          this.pScrollDistance = Math.round(this.listContainer?.nativeElement.scrollLeft);
          this.checkScrollingControls();
        });
    }
  }

  ngOnDestroy(): void {
    this.unSubscribeAll.next();
    this.unSubscribeAll.complete();
  }

  /**
   * Clear the status of the component.
   *
   * @param $event
   */
  clear($event?: Event) {
    $event?.stopImmediatePropagation();
    $event?.preventDefault();
    $event?.preventDefault();
    this.itemsSelected = [];
    this.internalItems?.forEach((item) => (item.selected = false));
  }

  /**
   * Handle the click/tap of the cbip.
   *
   * @param item
   */
  toggleCheck(item: any, index: number) {
    item.selected = !item.selected;
    if (item.selected) {
      this.scrollToChip(index);
    }
    if (this.isMultiSelect) {
      if (item.selected) {
        this.itemsSelected.push(item);
      } else {
        const index = this.itemsSelected.indexOf(item);
        if (index !== -1) {
          this.itemsSelected.splice(index, 1);
        }
      }
    } else {
      this.internalItems?.forEach((internalItem) =>
        internalItem.id !== item.id ? (internalItem.selected = false) : null
      );
      if (item.selected) {
        this.itemsSelected = [item];
      } else {
        this.itemsSelected = [];
      }
    }
    this.onChange(this.value);
  }

  /**
   * Converted the ids in selected items.
   *
   * @param val
   *  values
   */
  protected convertValues(val: any[]) {
    this.itemsSelected = [];

    this.internalItems?.forEach((item) => (item.selected = false));
    if (val && Array.isArray(val)) {
      val.forEach((value) => {
        const item = this.internalItems?.find((internalItem) => internalItem.value === value);
        if (!item) {
          return;
        }
        item.selected = true;
        this.itemsSelected.push(item);
      });
    }
    this.cdr.detectChanges();
  }

  /** Handles click events on the pagination arrows. */
  protected handlePaginatorClick(direction: ScrollDirection) {
    this.stopInterval();
    this.scrollChips(direction);
  }

  /** Stops the currently-running paginator interval.  */
  protected stopInterval() {
    this.stopScrolling.next();
  }

  /**
   * Handles the user pressing down on one of the paginators.
   * Starts scrolling the header after a certain amount of time.
   * @param direction In which direction the paginator should be scrolled.
   */
  protected handlePaginatorPress(direction: ScrollDirection, mouseEvent?: MouseEvent) {
    // Don't start auto scrolling for right mouse button clicks. Note that we shouldn't have to
    // null check the `button`, but we do it so we don't break tests that use fake events.
    if (mouseEvent && mouseEvent.button != null && mouseEvent.button !== 0) {
      return;
    }
    // Avoid overlapping timers.
    this.stopInterval();

    // Start a timer after the delay and keep firing based on the interval.
    timer(CHIPS_SCROLL_DELAY, CHIPS_SCROLL_INTERVAL)
      // Keep the timer going until something tells it to stop or the component is destroyed.
      .pipe(takeUntil(merge(this.stopScrolling, this.unSubscribeAll)))
      .subscribe(() => {
        const { maxScrollDistance, distance } = this.scrollChips(direction);

        // Stop the timer if we've reached the start or the end.
        if (distance === 0 || distance >= maxScrollDistance) {
          this.stopInterval();
        }
      });
  }

  /**
   * Moves the tab list in the 'before' or 'after' direction (towards the beginning of the list or
   * the end of the list, respectively). The distance to scroll is computed to be a third of the
   * length of the tab list view window.
   *
   * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
   * should be called sparingly.
   */
  private scrollChips(direction: ScrollDirection) {
    const viewLength = this.listContainer?.nativeElement.offsetWidth;

    // Move the scroll distance one-third the length of the tab list's viewport.
    const scrollAmount = ((direction == 'before' ? -1 : 1) * viewLength) / 3;

    return this.scrollTo(this.scrollDistance + scrollAmount);
  }
  /**
   * Handles and convert the new values of the items to shows.
   * @param {ItemListForFilters[]} items
   * New items to handles.
   */
  private handleItems(items: ItemListForFilters[]) {
    this.internalItems = items;
    if (this.initialValue) {
      this.convertValues(Array.isArray(this.initialValue) ? this.initialValue : [this.initialValue]);
      this.initialValue = undefined;
    }
    this.cdr.detectChanges();
    setTimeout(() => this.ngAfterViewInit(), 200);
  }

  /**
   * Evaluate whether the pagination controls should be displayed. If the scroll width of the
   * tab list is wider than the size of the header container, then the pagination controls should
   * be shown.
   *
   * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
   * should be called sparingly.
   */
  private checkPaginationEnabled() {
    if (this.rowWrap()) {
      this.showPaginationControls.set(false);
      return;
    }
    const isEnabled = this.listContainer?.nativeElement.scrollWidth > this.listContainer?.nativeElement.offsetWidth;

    if (!isEnabled) {
      this.scrollDistance = 0;
    } else {
      const indexSelected = this.internalItems?.findIndex((item) => item.selected);
      if (indexSelected !== -1 && indexSelected !== undefined) {
        setTimeout(() => this.scrollToChip(indexSelected), 200);
      }
    }

    this.showPaginationControls.set(isEnabled);
    this.checkScrollingControls();
  }

  /**
   * Determines what is the maximum length in pixels that can be set for the scroll distance. This
   * is equal to the difference in width between the tab list container and tab header container.
   *
   * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
   * should be called sparingly.
   */
  private getMaxScrollDistance(): number {
    const lengthOfTabList = this.listContainer?.nativeElement.scrollWidth || 0;
    const viewLength = this.listContainer?.nativeElement.offsetWidth || 0;
    return lengthOfTabList - viewLength || 0;
  }

  /**
   * Scrolls the header to a given position.
   * @param position Position to which to scroll.
   * @returns Information on the current scroll distance and the maximum.
   */
  private scrollTo(position: number) {
    const maxScrollDistance = this.getMaxScrollDistance();
    this.pScrollDistance = Math.max(0, Math.min(maxScrollDistance, position));

    this.checkScrollingControls();
    if (this.listContainer) {
      this.listContainer.nativeElement.scrollTo({
        left: Math.round(this.pScrollDistance),
        top: 0,
        behavior: 'smooth',
      });
    }

    return { maxScrollDistance, distance: this.pScrollDistance };
  }

  private scrollToChip(index: number): void {
    if (!this.showPaginationControls) {
      return;
    }
    const chipElement = (this.chipsContainers as any)?.toArray()[index]?.nativeElement;
    if (!chipElement) {
      return;
    }

    if (chipElement.offsetLeft < this.listContainer?.nativeElement.scrollLeft) {
      this.scrollDistance = chipElement.offsetLeft - 5;
      return;
    }

    const endElement = chipElement.offsetLeft + chipElement.offsetWidth;
    const viewPortEnd = this.listContainer?.nativeElement.scrollLeft + this.listContainer?.nativeElement.offsetWidth;
    if (endElement > viewPortEnd) {
      this.scrollDistance += endElement - viewPortEnd + 5;
      return;
    }
  }

  /**
   * Evaluate whether the before and after controls should be enabled or disabled.
   * If the header is at the beginning of the list (scroll distance is equal to 0) then disable the
   * before button. If the header is at the end of the list (scroll distance is equal to the
   * maximum distance we can scroll), then disable the after button.
   *
   * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
   * should be called sparingly.
   */
  private checkScrollingControls() {
    // Check if the pagination arrows should be activated.
    this.disableScrollBefore.set(this.scrollDistance === 0);
    this.disableScrollAfter.set(this.scrollDistance >= this.getMaxScrollDistance());
  }
}
